import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { content, NAV } from './content';
import {
  parsePath,
  pathFor,
  readStoredLang,
  storeLang,
  detectLang,
} from './routes';
import { syncHead } from './head';
import Banner from './components/Banner';
import Nav from './components/Nav';
import LanguageSwitcher from './components/LanguageSwitcher';
import Home from './components/Home';
import About from './components/About';
import Contact from './components/Contact';
import Products from './components/Products';

// The site root carries no language of its own. Send visitors to the language
// they chose last time; failing that, the best match for their browser; and
// failing that, English. An explicit /xx/ URL always wins over both.
function resolveInitialRoute() {
  const parsed = parsePath(window.location.pathname);
  if (parsed) return parsed;

  const lang = readStoredLang() || detectLang();
  window.history.replaceState(null, '', pathFor(lang, 'home'));
  return { lang, page: 'home' };
}

function App() {
  const [{ lang, page }, setRoute] = useState(resolveInitialRoute);

  const navigate = useCallback((nextLang, nextPage) => {
    window.history.pushState(null, '', pathFor(nextLang, nextPage));
    setRoute({ lang: nextLang, page: nextPage });
    window.scrollTo(0, 0);
  }, []);

  // Back / forward buttons.
  useEffect(() => {
    const onPopState = () => {
      const parsed = parsePath(window.location.pathname);
      if (parsed) setRoute(parsed);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Remember the language for the next visit.
  useEffect(() => storeLang(lang), [lang]);

  useEffect(() => syncHead(lang, page), [lang, page]);

  const data = content[lang];

  const renderPage = () => {
    switch (page) {
      case 'products':
        return <Products lang={lang} />;
      case 'about':
        return <About data={data.about} />;
      case 'contact':
        return <Contact data={data.contact} />;
      case 'home':
      default:
        return <Home data={data.home} />;
    }
  };

  return (
    <div className="App">
      <Banner />
      <Nav page={page} lang={lang} onNavigate={navigate} />

      <div className="layout">
        <LanguageSwitcher lang={lang} page={page} onNavigate={navigate} />
        <main className="content" aria-label={NAV[lang][page]}>
          {renderPage()}
        </main>
      </div>
    </div>
  );
}

export default App;
